import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { CircularProgress, Card, Link as MuiLink } from '@material-ui/core'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { toast } from 'utils/notify'
import firebase from 'firebase/app'
import AppLogo from '../../assets/primary_logo.png'
import { colors } from 'utils/colors'
import { Box } from '@mui/material'
import { Link, useHistory, useLocation } from 'react-router-dom'
import PasswordField from 'components/PasswordField'
import ProgressButton from 'components/ProgressButton'

const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required')
    .test(
      'password-strength',
      'Password must contain at least one letter and one number',
      (value) => {
        return /^(?=.*[A-Za-z])(?=.*\d)/.test(value)
      }
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required')
})

const ResetPassword = () => {
  const classes = useStyles()
  const history = useHistory()
  const query = useQuery()
  const oobCode = query.get('oobCode') // Firebase's reset code from the URL
  const mode = query.get('mode') // Should be 'resetPassword'

  const [email, setEmail] = useState('')
  const [validCode, setValidCode] = useState(false)
  const [verifying, setVerifying] = useState(true)
  const [performingAction, setPerformingAction] = useState(false)

  useEffect(() => {
    // Verify the password reset code when component mounts
    const verifyResetCode = async () => {
      if (!oobCode || mode !== 'resetPassword') {
        toast.error('Invalid or missing reset code. Request a new link.')
        setVerifying(false)
        return
      }

      try {
        const email = await firebase.auth().verifyPasswordResetCode(oobCode)
        setEmail(email)
        setValidCode(true)
      } catch (error) {
        toast.error('This reset link is invalid or has expired.')
        console.error('Error verifying reset code:', error)
      } finally {
        setVerifying(false)
      }
    }

    verifyResetCode()
  }, [oobCode, mode])

  const onSubmit = async (values) => {
    try {
      setPerformingAction(true)
      await firebase.auth().confirmPasswordReset(oobCode, values.password)
      history.replace('/')
      toast.success('Password reset. You can now log in.')
    } catch (error) {
      if (error.code === 'auth/expired-action-code') {
        toast.error('Reset link has expired. Please request a new one.')
      } else if (error.code === 'auth/weak-password') {
        toast.error('Password is too weak. Choose a stronger one.')
      } else {
        toast.error('Password reset failed: ' + error.message)
      }
    } finally {
      setPerformingAction(false)
    }
  }

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: resetPasswordSchema,
    onSubmit: onSubmit
  })

  if (verifying) {
    return (
      <div className={classes.container}>
        <div className={classes.overlay}></div>
        <Card className={classes.card}>
          <div className={classes.logoContainer}>
            <img src={AppLogo} alt="MeetSymple Logo" className={classes.logo} />
          </div>
          <Box display="flex" justifyContent="center" mt={4} mb={4}>
            <CircularProgress />
          </Box>
          <Typography variant="body1" align="center">
            Verifying your reset link...
          </Typography>
        </Card>
      </div>
    )
  }

  if (!validCode) {
    return (
      <div className={classes.container}>
        <div className={classes.overlay}></div>
        <Card className={classes.card}>
          <div className={classes.logoContainer}>
            <img src={AppLogo} alt="MeetSymple Logo" className={classes.logo} />
          </div>
          <Typography variant="h5" component="h1" className={classes.header}>
            Invalid Reset Link
          </Typography>
          <Typography variant="body1" align="center" gutterBottom>
            The password reset link is invalid or has expired.
          </Typography>
          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/forgot-password"
              fullWidth
              className={classes.submitButton}>
              Request New Reset Link
            </Button>
          </Box>
          <Box mt={2} textAlign="center">
            <Typography variant="body2">
              <MuiLink component={Link} to="/" color="primary">
                Back to Login
              </MuiLink>
            </Typography>
          </Box>
        </Card>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <div className={classes.overlay}></div>
      <Card className={classes.card}>
        <div className={classes.logoContainer}>
          <img src={AppLogo} alt="MeetSymple Logo" className={classes.logo} />
        </div>
        <Typography variant="h5" component="h1" className={classes.header}>
          Reset Password
        </Typography>
        <Typography variant="body2" align="center" gutterBottom>
          Create a new password for <strong>{email}</strong>
        </Typography>

        <form onSubmit={formik.handleSubmit} className={classes.form}>
          <PasswordField label="New Password" name="password" formik={formik} />
          <PasswordField label="Confirm New Password" name="confirmPassword" formik={formik} />

          <ProgressButton
            type="submit"
            className={classes.submitButton}
            label="Reset Password"
            inProgress={performingAction}
            disabled={performingAction || !formik.isValid}
          />
        </form>

        <Box mt={2} textAlign="center">
          <Typography variant="body2">
            <MuiLink
              component={Link}
              to="/"
              color="primary"
              style={performingAction ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}>
              Back to Login
            </MuiLink>
          </Typography>
        </Box>
      </Card>
    </div>
  )
}

const useStyles = makeStyles({
  textField: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      transition: 'all 0.3s',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#15b4db'
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e0e0e0',
      borderWidth: '1px'
    }
  },
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '20px'
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    zIndex: 1
  },
  card: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: '450px',
    borderRadius: '16px',
    padding: '35px 40px',
    boxShadow: '0 15px 25px rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(10px)',
    background: 'rgba(255, 255, 255, 0.95)',
    animation: '$fadeIn 0.8s ease-out',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px'
  },
  logo: {
    height: '60px',
    width: 'auto',
    filter: 'drop-shadow(0 2px 5px rgba(0, 0, 0, 0.2))'
  },
  header: {
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: '24px',
    color: colors.indigo
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    marginTop: '16px'
  },
  submitButton: {
    width: '100%',
    marginTop: '24px',
    padding: '12px',
    height: '48px',
    borderRadius: '8px',
    fontWeight: '600',
    letterSpacing: '0.5px'
  }
})

export default ResetPassword
